"use client";

import { Modal, ModalContent, ModalBody } from "@heroui/react";
import { TaskStatus } from "@/types/challenges.types";
import { useGetCampaign, useGetCampaignUser, useQuestsMutations } from "@/hooks/challenges";
import { CAMPAIGN_ID, CAMPAIGN_GROUP_ID } from "@/config/campaign";
import { FEATURED_TASK_GUID } from "@/config/featured-task";
import { useConnection } from "@/contexts/connection-context";
import { MarkdownRenderer } from "./MarkdownRenderer";
import { QuestRenderer } from "./task-renderers/QuestRenderer";
import { X, Star } from "lucide-react";
import { useMemo } from "react";

interface QuestModalProps {
  isOpen: boolean;
  onClose: () => void;
  task: TaskStatus | null;
}

export const QuestModal = ({ isOpen, onClose, task }: QuestModalProps) => {
  const { walletAddress } = useConnection();
  const { data: campaign } = useGetCampaign(CAMPAIGN_ID, CAMPAIGN_GROUP_ID);
  const { data: challengesUser } = useGetCampaignUser(
    CAMPAIGN_ID,
    CAMPAIGN_GROUP_ID
  );
  const { taskMutation } = useQuestsMutations();

  const challengeTask = useMemo(() => {
    return campaign?.tasks?.find((t) => t.id === task?.taskGuid);
  }, [task?.taskGuid, campaign?.tasks]);

  if (!campaign || !task) return null;

  const maxPoints = typeof challengeTask?.points === 'string' ? parseFloat(challengeTask.points) : (challengeTask?.points || 0);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="3xl"
      placement="center"
      scrollBehavior="inside"
      hideCloseButton
      classNames={{
        backdrop: "bg-black/50 backdrop-blur-sm",
        wrapper: "z-50 flex items-center justify-center",
        base: "bg-[#0C1113] text-white mx-2 sm:mx-4 my-2 sm:my-4 max-h-[95vh] sm:max-h-[90vh] rounded-3xl w-[95vw] sm:w-auto",
        body: "p-0",
        header: "hidden",
      }}
    >
      <ModalContent>
        {() => (
          <>
            {/* Close Button */}
            <button
              type="button"
              aria-label="Close quest"
              onClick={onClose}
              className="absolute top-4 right-4 z-50 w-8 h-8 flex items-center justify-center text-white hover:bg-gray-700 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <ModalBody className="p-0">
              {/* Quest Banner with Task Frame Image */}
              <div className="relative h-64 w-full">
                <img
                  src="https://pulsarmoney.b-cdn.net/rhuna/task_frame.png"
                  alt="Quest Frame"
                  className="w-full h-full object-cover rounded-t-3xl"
                />
                
                {/* Points Badge - Top Left */}
                <div className="absolute top-4 left-4 bg-[#60FF8A] text-black px-3 py-1 rounded-lg font-bold flex items-center gap-1">
                  <Star className="w-4 h-4" />
                  +{maxPoints.toFixed(0)} pts
                </div>
              </div>

              {/* Quest Content */}
              <div className="p-6">
                {/* Quest Title Below Image */}
                <h2 className="text-2xl font-bold text-white mb-4">{task.title}</h2>

                {/* Description Section — suppressed for featured task; stepper handles its own copy */}
                {task.taskGuid !== FEATURED_TASK_GUID && (
                  <div className="mb-6">
                    <div className="text-gray-300">
                      <MarkdownRenderer className="text-gray-300">
                        {task.description || "Complete this quest to earn rewards and progress in your journey."}
                      </MarkdownRenderer>
                    </div>
                  </div>
                )}

                {/* Quest Renderer */}
                <div className="w-full">
                  <QuestRenderer
                    challengesUser={challengesUser}
                    task={task}
                    address={walletAddress || ""}
                    campaign={campaign}
                    completeTaskMutation={taskMutation}
                  />
                </div>
              </div>
            </ModalBody>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};